import { UserSessionsProps } from '@msc-b2c/interfaces/types';

/**
 * Sets a new active session for the user.
 *
 * This function deactivates the current active session for the current tab
 * and adds a new session object to the list of user sessions stored in localStorage.
 *
 * @param {UserSessionsProps} newSession - The new session object to be set as active.
 */
const setNewActiveSession = (newSession: UserSessionsProps): void => {
  removeSessionsOfCurrentTab();
  const userSessionsRaw = localStorage['userSessions'];
  const userSessions = JSON.parse(userSessionsRaw?.trim() ? userSessionsRaw : '[]');

  // Add the new session object
  userSessions.push(newSession);

  // Save the updated sessions back to localStorage
  localStorage['userSessions'] = JSON.stringify(userSessions);
};

/**
 * Creates and activates a new user session.
 *
 * This function builds a session object using the provided parameters and sets it
 * as the active session by invoking `setNewActiveSession`. It supports both
 * "full" and "light" login modes, and can originate from either "partnership" or
 * "mymsc" sources.
 *
 * @param {string} token - The authentication token for the session.
 * @param {'full' | 'light'} loginMode - The login mode used for the session.
 * @param {'partnership' | 'mymsc'} source - The origin of the session.
 * @param {string} [email] - The user's email address.
 * @param {string} [name] - The user's first name.
 * @param {string} [surname] - The user's last name.
 * @param {string} [bookingNumber] - The booking number associated with the session.
 * @param {string} [cruiseId] - The cruise identifier.
 * @param {string} [crmId] - The CRM identifier.
 * @param {string} [goldenId] - The Golden Record identifier.
 * @param {string} [passengerId] - The passenger identifier.
 * @param {string[]} [allCruisesID] - A list of all associated cruise IDs.
 * @param {string} [dateOfBirth] - The user's date of birth (ISO string format).
 * @param {string} [countryOfResidence] - The user's country of residence.
 *
 * @returns {void} This function does not return a value.
 */
export const createNewSession = (
  token: string,
  loginMode: 'full' | 'light',
  source: 'partnership' | 'mymsc',
  email?: string,
  name?: string,
  surname?: string,
  bookingNumber?: string,
  cruiseId?: string,
  crmId?: string,
  goldenId?: string,
  passengerId?: string,
  allCruisesID?: string[],
  dateOfBirth?: string,
  countryOfResidence?: string
): void =>
  setNewActiveSession({
    crmId,
    cruiseId,
    goldenId,
    email,
    name,
    surname,
    passengerId,
    tabId: sessionStorage['tabId'],
    bookingNumber,
    token,
    loginMode,
    active: true,
    allCruisesID,
    dateOfBirth,
    countryOfResidence,
    source,
  });

/**
 * Retrieves the active user session.
 *
 * This function fetches the current tab ID from the session storage and then
 * searches the user sessions stored in the local storage to find the active
 * session that matches the current tab ID.
 *
 * @returns {UserSessionsProps} The active user session that matches the current tab ID.
 */
export const getActiveSession = (): UserSessionsProps => {
  const currentTabId = sessionStorage['tabId'];
  return (
    JSON.parse(localStorage['userSessions'] ?? '[]').filter(
      (item: UserSessionsProps) => item.tabId === currentTabId && item.active
    )[0] ?? {}
  );
};

export const updateActiveSession = (key: string, value: string | string[] | boolean): void => {
  const currentTabId = sessionStorage['tabId'];
  const userSessions: UserSessionsProps[] = JSON.parse(localStorage['userSessions'] ?? '[]');
  const updatedSessions = userSessions.map((session) => {
    if (session.tabId === currentTabId && session.active) {
      return { ...session, [key]: value };
    }
    return session;
  });
  localStorage['userSessions'] = JSON.stringify(updatedSessions);
};

/**
 * Checks whether there is an active user session for the current browser tab.
 *
 * This function retrieves the current tab ID from `sessionStorage` and looks up
 * all stored user sessions from `localStorage` under the key `'userSessions'`.
 * If a session exists with a matching `tabId` and `active` status set to `true`,
 * the function returns `true`. Otherwise, it returns `false`.
 *
 * @returns {boolean} True if an active session exists for the current tab, otherwise false.
 */
export const hasActiveSession = (): boolean => {
  const currentTabId = sessionStorage['tabId'];
  const userSessions = JSON.parse(localStorage['userSessions'] ?? '[]');

  return userSessions.some((item: UserSessionsProps) => item.tabId === currentTabId && item.active);
};

/**
 * Removes a user session by token.
 *
 * This function retrieves the list of user sessions stored in localStorage
 * and filters out the session that matches the provided token.
 *
 * @param {string} token - The token of the session to be removed.
 */
export const removeSessionByToken = (token: string): void => {
  const userSessions = JSON.parse(localStorage['userSessions'] ?? '[]');
  const updatedSessions = userSessions.filter(
    (session: UserSessionsProps) => session.token !== token
  );

  localStorage['userSessions'] = JSON.stringify(updatedSessions);
};

/**
 * Removes all user sessions associated with the current tab.
 *
 * This function retrieves the current tab ID from session storage and
 * calls `removeSessionsByTabId` to remove all sessions associated with that tab ID.
 */
export const removeSessionsOfCurrentTab = (): void => {
  const tabId = sessionStorage.getItem('tabId');
  if (tabId) removeSessionsByTabId(tabId);
};

/**
 * Removes user sessions by tab ID.
 *
 * This function retrieves the list of user sessions stored in localStorage
 * and filters out the sessions that match the provided tab ID.
 *
 * @param {string} tabId - The tab ID of the sessions to be removed.
 */
export const removeSessionsByTabId = (tabId: string): void => {
  localStorage['userSessions'] = JSON.stringify(
    JSON.parse(localStorage['userSessions'] ?? '[]').filter(
      (session: UserSessionsProps) => session.tabId !== tabId
    )
  );
};
